import React, { useRef, useEffect, useCallback, useState } from 'react';
import * as ViewModel from '@/core/presenter/viewModel';
import { KOREAN_PATTERN, NUMBER_ONLY_PATTERN, testPassword, ID_PATTERN, NICKNAME_PATTERN } from '@/utils/Validator';

interface Props {
  id?: string;
  type: string;
  value?: string;
  placeholder?: string;
  validatorType: ViewModel.InputValidatorType;
  tabIndex?: number;
  style?: React.CSSProperties;
  defaultFocus?: boolean;
  maxLength?: number;
  className?: string;
  disabled?: boolean;
  defaultValue?: string;
  pattern?: string;
  onClick?: (evt: React.SyntheticEvent<HTMLInputElement>) => void;
  onFocus?: (evt: React.SyntheticEvent<HTMLInputElement>) => void;
  onBlur?: (evt: React.SyntheticEvent<HTMLInputElement>) => void;
  onChange?: (value: React.SyntheticEvent<HTMLInputElement>) => void;
  onChangeValue?: (value: string) => void;
  onPressEnter?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

const Input = ({
  id,
  type,
  value,
  placeholder,
  validatorType,
  tabIndex,
  style,
  defaultFocus,
  maxLength,
  className,
  disabled,
  defaultValue,
  pattern,
  onChange,
  onChangeValue,
  onFocus,
  onBlur,
  onClick,
  onPressEnter,
  onKeyDown,
  onKeyPress,
}: Props): JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [curValue, setCurValue] = useState(value || '');

  useEffect(() => {
    if (defaultFocus) inputRef.current?.focus();
  }, [defaultFocus]);

  useEffect(() => {
    if (typeof value !== 'undefined') setCurValue(value);
  }, [value]);

  useEffect(() => {
    if (inputRef.current) inputRef.current.type = type;
  }, [type]);

  const handleChange = useCallback(
    (evt: React.SyntheticEvent<HTMLInputElement>) => {
      let val = evt.currentTarget.value;

      let isValidInput = false;
      if (val.length === 0) isValidInput = true;
      if (validatorType === ViewModel.InputValidatorType.KOREAN_ONLY && KOREAN_PATTERN.test(val)) isValidInput = true;
      if (validatorType === ViewModel.InputValidatorType.NUMBER_ONLY && NUMBER_ONLY_PATTERN.test(val))
        isValidInput = true;
      if (validatorType === ViewModel.InputValidatorType.PASSWORD && testPassword(val)) isValidInput = true;
      if (validatorType === ViewModel.InputValidatorType.ALPHABET_NUMBER && ID_PATTERN.test(val)) isValidInput = true;
      if (validatorType === ViewModel.InputValidatorType.NICKNAME && NICKNAME_PATTERN.test(val)) isValidInput = true;
      if (validatorType === ViewModel.InputValidatorType.ALLOW_ALL) isValidInput = true;

      if (isValidInput) {
        if (typeof maxLength !== 'undefined') {
          val = val.substring(0, maxLength);
        }

        setCurValue(val);
        if (onChange) onChange(evt);
        if (onChangeValue) onChangeValue(val);
      }
    },
    [maxLength, validatorType, onChange, onChangeValue],
  );

  const handleKeyUp = useCallback(
    (evt: React.KeyboardEvent<HTMLInputElement>) => {
      if (evt.code === 'Enter') {
        if (onPressEnter) {
          onPressEnter();
          inputRef.current?.blur();
        }
      }
    },
    [onPressEnter],
  );

  return (
    <input
      id={id}
      type={type}
      value={curValue}
      placeholder={placeholder}
      tabIndex={tabIndex}
      style={style}
      className={className}
      disabled={disabled}
      defaultValue={defaultValue}
      pattern={pattern}
      onFocus={onFocus}
      onBlur={onBlur}
      onClick={onClick}
      onChange={handleChange}
      onKeyUp={handleKeyUp}
      onKeyDown={onKeyDown}
      onKeyPress={onKeyPress}
      ref={inputRef}
    />
  );
};

export default Input;
